import { Component, EventEmitter, Injector, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { PaymentService } from 'app/core/http/services/payment.service';
import { Encounter } from 'app/core/interfaces/encounter';
import { Payment } from 'app/core/interfaces/payment';
import { PatientAdapter } from 'app/core/adapters/patient.adapter';
import { Patient } from 'app/core/interfaces/patient';
import { BaseComponent } from 'app/modules/shared/components/base.component';
import { pixCpfValidator } from 'app/core/validators/pixCpfValidator';
import { getHttpErrorMessage } from 'app/core/utils/http-error-message';

@Component({
  selector: 'src-payment-credit-card',
  templateUrl: './payment-credit-card.component.html',
  styleUrls: ['./payment-credit-card.component.css'],
})
export class PaymentCreditCardComponent extends BaseComponent implements OnInit {
  creditCardForm: FormGroup;
  customerInfoForm: FormGroup;

  @Input() encounter!: Encounter;
  @Input() forceNew = false;

  public submitted = false;
  public paymentErrorMessage = 'Falha ao realizar o pagamento. Confira seus dados e tente novamente!';

  @Output() onConfirmPayment = new EventEmitter<Payment>();
  patientProfile!: Patient;

  constructor(
    private injector: Injector,
    private fb: FormBuilder,
    private paymentService: PaymentService,
    private patientAdapter: PatientAdapter,
  ) {
    super(injector);
    this.creditCardForm = this.fb.group({
      holderName: ['', [Validators.required, Validators.minLength(3), Validators.pattern('^[A-Za-zÀ-ÖØ-öø-ÿ ]+$')]],
      number: ['', [Validators.required, Validators.pattern('^[0-9]{13,16}$')]],
      expiryYear: ['', [Validators.required, Validators.pattern('^[0-9]{4}$')]],
      expiryMonth: ['', [Validators.required, Validators.pattern('^(0[1-9]|1[0-2])$')]],
      ccv: ['', [Validators.required, Validators.pattern('^[0-9]{3}$')]],
      installments: [1, [Validators.required, Validators.min(1), Validators.max(12)]],
    });

    this.customerInfoForm = this.fb.group({
      name: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      documentType: ['', [Validators.required]],
      cpfCnpj: ['', [Validators.required, pixCpfValidator]],
      postalCode: ['', [Validators.required, Validators.pattern('^[0-9]{8}$')]],
      addressNumber: ['', Validators.required],
      addressComplement: [''],
      city: ['', Validators.required],
      state: ['', [Validators.required, Validators.pattern('^[A-Za-z]{2}$')]],
      phone: ['', [Validators.required, Validators.pattern('^[0-9]{10,11}$')]],
    });
  }

  async ngOnInit() {
    this.setValueToFormCustomer();
  }

  setValueToFormCustomer() {
    this.patientProfile = this.getPaymentPatient();

    this.customerInfoForm.patchValue({
      name: this.patientProfile.name,
      email: this.patientProfile.email,
      cpfCnpj: this.onlyNumbers(this.patientProfile.cpf),
      documentType: 'CPF',
      postalCode: this.onlyNumbers(this.patientProfile.address.cep),
      addressNumber: this.patientProfile.address.number,
      addressComplement: this.patientProfile.address.complement,
      city: this.patientProfile.address.city,
      state: this.patientProfile.address.state,
      phone: this.normalizePhone(this.patientProfile.phone || ''),
    });
  }

  createPayment() {
    this.normalizePaymentForms();

    if (this.creditCardForm.invalid || this.customerInfoForm.invalid) {
      this.creditCardForm.markAllAsTouched();
      this.customerInfoForm.markAllAsTouched();
      return;
    }

    const creditCardFormValue = this.creditCardForm.value;
    const customerInfo = this.customerInfoForm.value;
    const encounterId = this.encounter.encounter_id || '';

    customerInfo.patient_id = this.patientProfile.patient_id;
    customerInfo.birthDate = this.patientProfile.birthDate;
    customerInfo.cpfCnpj = this.onlyNumbers(customerInfo.cpfCnpj);
    customerInfo.phone = this.normalizePhone(customerInfo.phone);
    customerInfo.postalCode = this.onlyNumbers(customerInfo.postalCode);
    this.submitted = true;

    const payment: Payment = {
      encounter_id: encounterId,
      customer_info: customerInfo,
      payment_method: 'CREDIT_CARD',
      force_new: this.forceNew,
      installments: Number(creditCardFormValue.installments || 1),
      credit_card: {
        holderName: String(creditCardFormValue.holderName || '').trim(),
        number: this.onlyNumbers(creditCardFormValue.number),
        expiryYear: String(creditCardFormValue.expiryYear || '').trim(),
        expiryMonth: String(creditCardFormValue.expiryMonth || '').trim(),
        ccv: this.onlyNumbers(creditCardFormValue.ccv),
      },
    };

    this.paymentService.create(payment).subscribe({
      next: (payment: Payment) => {
        this.showSuccessMessage = true;
        this.submitted = false;
        this.onConfirmPayment.emit(payment);
      },
      error: (err) => {
        this.paymentErrorMessage = getHttpErrorMessage(err, 'Falha ao realizar o pagamento. Confira seus dados e tente novamente!');
        this.showErrorMessage = true;
        this.submitted = false;
      },
    });
  }

  canSubmitPayment(): boolean {
    return !this.showSuccessMessage && !this.submitted && this.creditCardForm.valid && this.customerInfoForm.valid;
  }

  shouldShowInvalidDocumentMessage(): boolean {
    const documentControl = this.customerInfoForm.get('cpfCnpj');

    return this.isFullDocumentTyped() && !!documentControl?.hasError('documentInvalid');
  }

  isFullDocumentTyped(): boolean {
    const documentLength = this.onlyNumbers(this.customerInfoForm.get('cpfCnpj')?.value).length;

    return documentLength === 11 || documentLength === 14;
  }

  getInvalidDocumentMessage(): string {
    const documentLength = this.onlyNumbers(this.customerInfoForm.get('cpfCnpj')?.value).length;

    return documentLength === 14 ? 'CNPJ informado não é válido' : 'CPF informado não é válido';
  }

  private onlyNumbers(value: string): string {
    return String(value || '').replace(/\D/g, '');
  }

  private normalizePhone(value: string): string {
    const phone = this.onlyNumbers(value);

    if (phone.startsWith('55') && phone.length > 11) {
      return phone.substring(2);
    }

    return phone;
  }

  private getPaymentPatient(): Patient {
    if (this.currentUser?.type === 'patient') {
      return this.patientAdapter.adapterToModel(this.currentUser.user);
    }

    const patientDetails = this.encounter?.patient_details || {};
    const patient: any = this.encounter?.patient || {};
    const address = this.getPatientAddress(patientDetails);

    return {
      patient_id: patientDetails.patient_id || patient.patient_id || '',
      name: patientDetails.name || patient.name || '',
      gender: patientDetails.gender || 'Male',
      birthDate: patientDetails.birthDate || patient.birthDate || '',
      cpf: patientDetails.cpf || patientDetails.cpfCnpj || patientDetails.document || patient.cpf || '',
      mobile: patientDetails.mobile || patientDetails.phone || patient.phone || '',
      phone: patientDetails.phone || patientDetails.mobile || patient.phone || '',
      email: patientDetails.email || patient.email || '',
      active: true,
      address,
    } as Patient;
  }

  private getPatientAddress(patientDetails: any) {
    const rawAddress = Array.isArray(patientDetails.address) ? patientDetails.address[0] : patientDetails.address || {};
    const line = rawAddress.line || [];

    return {
      cep: rawAddress.cep || rawAddress.postalCode || patientDetails.postalCode || '',
      number: rawAddress.number || line[1] || patientDetails.addressNumber || '',
      complement: rawAddress.complement || line[2] || patientDetails.addressComplement || '',
      city: rawAddress.city || patientDetails.city || '',
      state: rawAddress.state || patientDetails.state || '',
      country: rawAddress.country || patientDetails.country || '',
      street: rawAddress.street || line[0] || patientDetails.street || '',
    };
  }

  private normalizePaymentForms() {
    const expiryMonth = this.onlyNumbers(this.creditCardForm.get('expiryMonth')?.value);

    this.creditCardForm.patchValue(
      {
        number: this.onlyNumbers(this.creditCardForm.get('number')?.value),
        expiryMonth: expiryMonth ? expiryMonth.padStart(2, '0') : '',
        expiryYear: this.onlyNumbers(this.creditCardForm.get('expiryYear')?.value),
        ccv: this.onlyNumbers(this.creditCardForm.get('ccv')?.value),
      },
      { emitEvent: false },
    );

    this.customerInfoForm.patchValue(
      {
        cpfCnpj: this.onlyNumbers(this.customerInfoForm.get('cpfCnpj')?.value),
        postalCode: this.onlyNumbers(this.customerInfoForm.get('postalCode')?.value),
        phone: this.normalizePhone(this.customerInfoForm.get('phone')?.value),
        state: String(this.customerInfoForm.get('state')?.value || '')
          .trim()
          .toUpperCase(),
      },
      { emitEvent: false },
    );
  }
}
